import React, { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { Mic, Upload } from "lucide-react";
import { v4 as uuid } from "uuid";
import { IoClose } from "react-icons/io5";
import { getIdToken } from "../../utils/firebaseAuth";
import axios from "axios";
import { FaMusic } from "react-icons/fa";
import { Spinner } from "@jammable/ui-components/Loader";
import { MdBlock } from "react-icons/md";
import useAuthModal from "../../hooks/useAuthModal";
import { useAuth } from "../../context/AuthProvider";
import { useModal } from "../../context/ModalContext";
import dynamic from "next/dynamic";
import { canUpsellToCreator } from "../upsell/CreatorUpsellSheet";
import { useT } from "@jammable/ui-core/src/i18n";
import {
  TrainStepTitle,
  TrainSummaryCard,
  filesLabel,
} from "./TrainSummaryCard";
import { PlayKept, SampleWave } from "./Sample";
import ClipEditor from "./ClipEditor";
import MicrophoneRecordViewJs from "../modals/MicrophoneRecordView";
import {
  analyse,
  clock,
  fingerprint,
  keepOf,
  MAX_TRAINING_SECONDS,
  MIN_TRAINING_SECONDS,
  RECOMMENDED_TRAINING_SECONDS,
  secsOf,
} from "@/lib/train/clips";

const MicrophoneRecordView = MicrophoneRecordViewJs;

// Opens only when they're out of training passes (pricing, Stripe Elements).
const UpgradeModal = dynamic(() => import("../modals/UpgradeModal"), {
  ssr: false,
});

// "3 minutes", from the limits in lib/train/clips. `t` is useT()'s.
const minutes = (t, secs) => t("train.minutes", { n: Math.round(secs / 60) });

const TrainUploadStage = ({
  setStage,
  files,
  setFiles,
  scannedFiles,
  setScannedFiles,
  clips,
  setClips,
  passesLabel,
}) => {
  const t = useT();
  const accept = {
    "audio/mp3": [],
    "audio/wav": [],
    "audio/x-m4a": [],
    "audio/mpeg": [],
  };
  const acceptedFileTypes = [
    "audio/mp3",
    "audio/wav",
    "audio/x-m4a",
    "audio/mpeg",
  ];

  const { user } = useAuth();
  const checkDuration = async (file) => {
    return new Promise((resolve, reject) => {
      try {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);

        audio.addEventListener("loadedmetadata", () => {
          if (!isFinite(audio.duration) || isNaN(audio.duration)) {
            reject(new Error("Error checking audio duration"));
          } else {
            resolve(audio.duration);
          }
        });
        audio.addEventListener("error", () => {
          console.error("Error checking audio duration");
          reject(new Error("Error checking audio duration"));
        });
      } catch (error) {
        console.error("Error checking audio duration", error);
        reject(new Error("Error checking audio duration"));
      }
    });
  };

  const { checkAuthAndExecute } = useAuthModal();
  // Fingerprints of dropped files not yet in `files` (handleDropFiles).
  const pending = useRef(new Set());
  const { openModal } = useModal();

  const updateScannedFiles = (fileId, updateData, file) => {
    setScannedFiles((prevFiles) => {
      const fileExists = prevFiles.some((file) => file.id === fileId);
      if (fileExists) {
        return prevFiles.map((file) =>
          file.id === fileId ? { ...file, ...updateData } : file,
        );
      } else {
        return [...prevFiles, { id: fileId, ...updateData, ...file }];
      }
    });
  };

  // `recorded`: takes from the Record tab, whose type is whatever the
  // browser records in (webm, mp4), not one of the upload types.
  const handleDropFiles = async (acceptedFiles, { recorded = false } = {}) => {
    if (!checkAuthAndExecute()) return false;

    // No training passes (and not on Creator or Power, which train without
    // them). Without a plan: the training paywall now, rather than after
    // they've uploaded, named and pictured a voice they can't train. Basic
    // and secret-plan subscribers upload first, told up front that training
    // comes with Creator; the Start button offers the upgrade
    // (CreatorUpsellSheet). The server enforces it either way
    // (startTrainingFull).
    if (
      passesLabel !== "Unlimited" &&
      !(Number(passesLabel) > 0) &&
      !canUpsellToCreator(user)
    ) {
      openModal("upgrade-modal", <UpgradeModal from="train-upload" />, {
        className: "tw-p-0",
      });
      return false;
    }
    if (acceptedFiles.length === 0) {
      toast.error(t("train.upload.invalidType"));
      return;
    }

    // Drop files whose audio is already in the list (lib/train/clips
    // fingerprint). Repeats used to count toward the 6:00 minimum, so people
    // added one clip many times to reach it - one voice had a 19 s clip 22
    // times - and trained on 6 minutes that were really 19 seconds.
    const typed = acceptedFiles.filter(
      (file) => recorded || acceptedFileTypes.includes(file.type),
    );
    // `pending` covers files from an earlier drop that are still being
    // measured and so aren't in `files` yet.
    const known = new Set([
      ...files.map((f) => f.fingerprint).filter(Boolean),
      ...pending.current,
    ]);
    const fresh = [];
    const repeats = [];
    for (const file of typed) {
      file.fingerprint = await fingerprint(file);
      if (file.fingerprint && known.has(file.fingerprint)) {
        repeats.push(file.name);
        continue;
      }
      if (file.fingerprint) {
        known.add(file.fingerprint);
        pending.current.add(file.fingerprint);
      }
      fresh.push(file);
    }
    if (repeats.length) {
      const names =
        repeats.length <= 3
          ? repeats.map((n) => `"${n}"`).join(", ")
          : t("train.upload.nFiles", { n: repeats.length });
      toast(
        t(
          repeats.length === 1
            ? "train.upload.skippedOne"
            : "train.upload.skippedMany",
          { names },
        ),
        { duration: 6000 },
      );
    }
    if (fresh.length === 0) return;

    const promises = fresh.map(async (file) => {
      file.id = uuid();
      // The silence analysis decodes the file anyway; its length is
      // whichever answer comes first. (An <audio> element alone never
      // loads in a background tab, so dropping files and switching tabs
      // left them hanging.)
      const analysis = analyse(file);
      analysis.then((a) =>
        setClips((prev) => ({
          ...prev,
          [file.id]: { analysis: a, cuts: [] },
        })),
      );
      file.duration = await Promise.any([
        checkDuration(file),
        analysis.then(
          (a) => a?.duration ?? Promise.reject(new Error("no length")),
        ),
      ]);
      return file;
    });

    // Not `files`: that is the list already added, read above.
    const settled = await Promise.allSettled(promises);

    const usableFiles = settled
      .filter((file) => {
        return file.status === "fulfilled";
      })
      .map((file) => file.value);

    if (settled.some((file) => file.status === "rejected")) {
      toast.error(t("train.upload.someInvalid"));
    }

    setFiles((prevFiles) => [...prevFiles, ...usableFiles]);
    for (const file of fresh) pending.current.delete(file.fingerprint);
  };

  const onDrop = async (acceptedFiles) => {
    await handleDropFiles(acceptedFiles);
  };

  // Files can be dropped anywhere on step 1; the Upload button opens the
  // picker.
  const dz = useDropzone({ onDrop, accept, noClick: true, noKeyboard: true });

  // The recorder shows under the buttons once they tap Record.
  const [recording, setRecording] = useState(false);
  // The file being trimmed, if any.
  const [editing, setEditing] = useState(null);
  const addRecording = (blob) => {
    const n = files.filter((f) => f.recorded).length + 1;
    const ext = /mp4|aac|m4a/.test(blob.type) ? "m4a" : "webm";
    const file = new File([blob], `Recording ${n}.${ext}`, {
      type: blob.type || "audio/webm",
    });
    file.recorded = true;
    handleDropFiles([file], { recorded: true });
  };

  const removeFile = (fileId) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
    setScannedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileId),
    );
    setClips((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const keptOf = (file) => secsOf(keepOf(clips[file.id], file.duration));
  // The length rule counts what's kept, not file length, and each file's
  // audio once (repeats are never added).
  const duration = files.reduce((acc, file) => acc + keptOf(file), 0);

  // File scanning logic
  const scanFile = async (file) => {
    const token = await getIdToken();

    if (!token) {
      throw new Error("User must be signed in");
    }

    let audioUrl;
    if (!file.url) {
      // Never two rows for one file. processFile can run twice for a file
      // (the effect below re-runs on every `files` change and reads a stale
      // scannedFiles), and a second row was then updated with the same URL by
      // updateScannedFiles. Nothing stopped that: 256 voices in the 30 days to
      // 2026-10-09 sent one URL 2-19 times.
      setScannedFiles((prevFiles) =>
        prevFiles.some((f) => f.id === file.id)
          ? prevFiles
          : [
              ...prevFiles,
              { id: file.id, status: "uploading", isBlocked: null },
            ],
      );

      // A voice's training audio, not a cover: the cover allowance doesn't
      // apply (training has its own, checked when the voice is submitted).
      const response = await axios.get("/api/s3/modelS3PresignedUrl", {
        headers: { Authorization: token },
        params: { purpose: "training" },
      });
      const { url, absoluteUrl } = response.data;
      await axios.put(url, file, {
        headers: { "Content-Type": file.type },
      });
      audioUrl = absoluteUrl;
    } else {
      audioUrl = file.url;
    }

    updateScannedFiles(file.id, {
      status: "success",
      isBlocked: false,
      blockedLabels: null,
      outputs: [],
      url: audioUrl,
      ...file,
      // A File's name, size and type are prototype getters, so `...file`
      // copies only id and duration. Without this every training file was
      // saved as "training_file_N_<ms>.mp3" (all 37,387 in the last 30 days).
      name: file.name,
    });

    return file.id;
  };

  // A failed upload says why on its row. Out of the plan's allowance opens
  // the training paywall (once per drop, however many files hit it); the
  // burst limit asks them to wait; anything else can be retried.
  const paywallShownAt = useRef(0);
  const uploadFailed = (file, error) => {
    const code = error?.response?.status;
    const message = error?.response?.data?.message || "";
    let errorLabel = t("train.upload.failed");
    let canRetry = true;
    if (code === 429 && /credit/i.test(message)) {
      errorLabel = t("train.upload.needsPlan");
      canRetry = false;
      if (Date.now() - paywallShownAt.current > 5000) {
        paywallShownAt.current = Date.now();
        openModal("upgrade-modal", <UpgradeModal from="train-upload" />, {
          className: "tw-p-0",
        });
      }
    } else if (code === 429) {
      errorLabel = t("train.upload.tooManyLabel");
      toast.error(t("train.upload.tooManyToast"));
    }
    console.warn("Training upload failed:", error);
    updateScannedFiles(file.id, {
      status: "error",
      isBlocked: false,
      blockedLabels: null,
      outputs: [],
      errorLabel,
      canRetry,
    });
  };

  // One upload per file, however often the effect below fires.
  const started = useRef(new Set());
  const processFile = async (file) => {
    if (started.current.has(file.id)) return;
    started.current.add(file.id);
    try {
      await scanFile(file);
    } catch (error) {
      uploadFailed(file, error);
    }
  };

  const retryFile = (file) => {
    started.current.delete(file.id);
    setScannedFiles((prev) => prev.filter((sf) => sf.id !== file.id));
    processFile(file);
  };

  useEffect(() => {
    files.forEach((file) => {
      if (!scannedFiles.some((scannedFile) => scannedFile.id === file.id)) {
        processFile(file);
      }
    });
  }, [files]);

  const goToNextStage = () => {
    const isErrored = Object.values(scannedFiles).some(
      (file) => file.status === "error",
    );

    const isBlocked = Object.values(scannedFiles).some(
      (file) => file.isBlocked === true,
    );
    const isProcessing = Object.values(scannedFiles).some(
      (file) =>
        file?.status === "scanning" ||
        file?.status === "created" ||
        file?.status === "uploading",
    );

    const analysing = files.some((file) => !(file.id in clips));
    if (analysing) {
      toast.error(t("train.upload.analysing"));
      return;
    }
    if (isBlocked) {
      toast.error(t("train.upload.blocked"));
      return;
    }
    if (isErrored) {
      toast.error(t("train.upload.errored"));
      return;
    }
    if (isProcessing) {
      toast.error(t("train.upload.processing"));
      return;
    }
    if (duration < MIN_TRAINING_SECONDS) {
      toast.error(
        t("train.upload.tooShort", {
          min: minutes(t, MIN_TRAINING_SECONDS),
          have: clock(duration),
        }),
      );
      return;
    }
    if (duration > MAX_TRAINING_SECONDS) {
      toast.error(
        t("train.upload.tooLong", {
          max: minutes(t, MAX_TRAINING_SECONDS),
          have: clock(duration),
        }),
      );
      return;
    }
    setStage(1);
  };

  const ready =
    duration >= MIN_TRAINING_SECONDS && duration <= MAX_TRAINING_SECONDS;
  // Enough to train, but less than voices usually need to sound their best.
  // A note, never a block.
  const short = ready && duration < RECOMMENDED_TRAINING_SECONDS;

  return (
    <div className="tw-grid tw-items-start tw-gap-5 lg:tw-grid-cols-[minmax(0,1fr)_380px] lg:tw-gap-7">
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
        <section
          {...dz.getRootProps()}
          className={`tw-flex tw-flex-col tw-gap-4 tw-rounded-[28px] tw-bg-jam-lav tw-p-4 tw-outline-none md:tw-rounded-[36px] md:tw-p-6 ${dz.isDragActive ? "tw-ring-4 tw-ring-jam-purple" : ""}`}
        >
          <input {...dz.getInputProps()} />
          <TrainStepTitle n={1} active>
            {t("train.upload.title")}
          </TrainStepTitle>

          {/* How much there is, and what's left to do. */}
          <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[26px] tw-bg-white tw-p-5">
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
              <span className="jam-display tw-text-[34px] tw-font-extrabold tw-tabular-nums tw-leading-none tw-tracking-[-0.03em]">
                {clock(duration)}
                <span className="tw-text-[17px] tw-font-extrabold tw-tracking-normal tw-text-jam-muted">
                  {" "}
                  {t("train.upload.ofMin", {
                    min: clock(MIN_TRAINING_SECONDS),
                  })}
                </span>
              </span>
              <span
                className={`tw-text-sm tw-font-extrabold ${
                  duration > MAX_TRAINING_SECONDS
                    ? "tw-text-red-500"
                    : ready
                      ? "tw-text-[#3E8A00]"
                      : "tw-text-jam-muted"
                }`}
              >
                {duration > MAX_TRAINING_SECONDS
                  ? t("train.upload.overLimit", {
                      over: clock(duration - MAX_TRAINING_SECONDS),
                      n: Math.round(MAX_TRAINING_SECONDS / 60),
                    })
                  : ready
                    ? t("train.upload.ready")
                    : t("train.upload.addMore", {
                        left: clock(MIN_TRAINING_SECONDS - duration),
                      })}
              </span>
            </div>
            <div className="tw-h-3.5 tw-overflow-hidden tw-rounded-full tw-bg-jam-lav">
              <div
                className={`tw-h-full tw-rounded-full tw-transition-all ${duration > MAX_TRAINING_SECONDS ? "tw-bg-red-400" : ready ? "tw-bg-[#7BC400]" : "tw-bg-jam-purple"}`}
                style={{
                  width: `${Math.min(100, (duration / MIN_TRAINING_SECONDS) * 100)}%`,
                }}
              />
            </div>
            {short && (
              <div className="tw-rounded-[16px] tw-bg-jam-lav tw-px-3.5 tw-py-2.5 tw-text-[13px] tw-font-bold tw-leading-snug tw-text-jam-ink">
                {t("train.upload.short", {
                  n: Math.round(RECOMMENDED_TRAINING_SECONDS / 60),
                  left: clock(RECOMMENDED_TRAINING_SECONDS - duration),
                })}
              </div>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-2.5">
            <button
              type="button"
              onClick={() => {
                setRecording(false);
                dz.open();
              }}
              className="tw-flex tw-h-[60px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-ink tw-text-[16px] tw-font-extrabold tw-text-white hover:tw-bg-jam-deep"
            >
              <Upload size={18} strokeWidth={2.5} />
              {t("train.upload.uploadFiles")}
            </button>
            <button
              type="button"
              onClick={() => setRecording((r) => !r)}
              aria-pressed={recording}
              className={`tw-flex tw-h-[60px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-text-[16px] tw-font-extrabold ${recording ? "tw-bg-jam-purple tw-text-white" : "tw-bg-white tw-text-jam-ink hover:tw-bg-jam-line"}`}
            >
              <Mic size={18} strokeWidth={2.5} />
              {t("train.upload.record")}
            </button>
          </div>

          {recording && (
            <MicrophoneRecordView
              variant="jam"
              onTake={addRecording}
              takeLabel={t("train.upload.addIt")}
              hint={t("train.upload.recordHint")}
            />
          )}

          {files?.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              {files.map((file) => {
                const scanStatus = scannedFiles.find((sf) => sf.id === file.id);
                const isBlocked = scanStatus?.isBlocked;
                const isError = scanStatus?.status === "error";
                const isScanning =
                  scanStatus?.status !== "success" &&
                  scanStatus?.status !== "error";
                const blockedLabel = scanStatus?.blockedLabels?.[0];
                const clip = clips[file.id];
                const keep = keepOf(clip, file.duration);
                const usedSecs = secsOf(keep);
                const removedSecs = file.duration - usedSecs;
                return (
                  <div
                    key={file.id}
                    className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[26px] tw-bg-white tw-p-4"
                  >
                    <div className="tw-flex tw-items-center tw-gap-3">
                      {isBlocked || isError ? (
                        <span className="tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100">
                          <MdBlock size={18} className="tw-text-red-500" />
                        </span>
                      ) : clip?.analysis ? (
                        <PlayKept file={file} keep={keep} label={file.name} />
                      ) : (
                        <span className="tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav">
                          {isScanning || !clip ? (
                            <Spinner
                              size={4}
                              className="tw-border-3 tw-border-jam-purple"
                            />
                          ) : (
                            <FaMusic size={13} className="tw-text-jam-purple" />
                          )}
                        </span>
                      )}
                      <div className="tw-min-w-0 tw-flex-grow">
                        <div className="tw-truncate tw-text-[15px] tw-font-extrabold">
                          {file.name}
                        </div>
                        <div
                          className={`tw-text-[13px] tw-font-bold ${isBlocked || isError ? "tw-text-red-500" : "tw-text-jam-muted"}`}
                        >
                          {isBlocked && blockedLabel
                            ? t("train.upload.blockedFrom", {
                                label: blockedLabel,
                              })
                            : isError
                              ? scanStatus?.errorLabel ||
                                t("train.upload.failed")
                              : !clip
                                ? t("train.upload.gettingReady")
                                : `${t("train.upload.ofVoice", { time: clock(usedSecs) })}${removedSecs >= 1 ? ` · ${t("train.upload.removed", { time: clock(removedSecs) })}` : ""}${isScanning ? ` · ${t("train.upload.uploading")}` : ""}`}
                        </div>
                      </div>
                      {clip?.analysis && (
                        <button
                          type="button"
                          onClick={() =>
                            setEditing(editing === file.id ? null : file.id)
                          }
                          aria-expanded={editing === file.id}
                          className={`tw-flex tw-h-10 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-0 tw-px-5 tw-text-[14px] tw-font-extrabold ${editing === file.id ? "tw-bg-jam-ink tw-text-white" : "tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line"}`}
                        >
                          {editing === file.id
                            ? t("train.upload.done")
                            : t("train.upload.trim")}
                        </button>
                      )}
                      {isError && scanStatus?.canRetry !== false && (
                        <button
                          type="button"
                          onClick={() => retryFile(file)}
                          className="tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-ink tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-white"
                        >
                          {t("train.upload.retry")}
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={t("train.upload.remove", {
                          name: file.name,
                        })}
                        onClick={() => removeFile(file.id)}
                        className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-jam-muted hover:tw-text-jam-ink"
                      >
                        <IoClose size={18} />
                      </button>
                    </div>
                    {clip?.analysis &&
                      (editing === file.id ? (
                        <ClipEditor
                          file={file}
                          clip={clip}
                          onCuts={(cuts) =>
                            setClips((prev) => ({
                              ...prev,
                              [file.id]: { ...prev[file.id], cuts },
                            }))
                          }
                        />
                      ) : (
                        <SampleWave clip={clip} keep={keep} />
                      ))}
                  </div>
                );
              })}
              <div className="tw-text-center tw-text-xs tw-font-semibold tw-text-jam-muted">
                {t("train.upload.silenceNote")}
              </div>
            </div>
          )}

          <div className="tw-grid tw-gap-2 tw-text-[13px] tw-font-bold sm:tw-grid-cols-3">
            {[
              [t("train.upload.tip1.title"), t("train.upload.tip1.sub")],
              [t("train.upload.tip2.title"), t("train.upload.tip2.sub")],
              [t("train.upload.tip3.title"), t("train.upload.tip3.sub")],
            ].map(([title, sub]) => (
              <div key={title} className="tw-rounded-[18px] tw-bg-white tw-p-3">
                <div className="tw-font-extrabold">{title}</div>
                <div className="tw-text-jam-muted">{sub}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-1 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-4 tw-opacity-70 md:tw-rounded-[36px] md:tw-p-6">
          <TrainStepTitle n={2}>{t("train.nameIt")}</TrainStepTitle>
          <div className="tw-pl-11 tw-text-[13px] tw-font-semibold tw-text-jam-muted">
            {t("train.upload.nextHint")}
          </div>
        </section>
      </div>

      <TrainSummaryCard
        title={t("train.upload.cardTitle")}
        rows={[
          [t("train.samples"), filesLabel(t, files.length, clock(duration))],
          [
            t("train.passesLeft"),
            // "Unlimited" is train.js's marker for Creator and Power, checked
            // below; only the label is translated.
            passesLabel === "Unlimited" ? t("train.unlimited") : passesLabel,
          ],
        ]}
        footnote={t(
          RECOMMENDED_TRAINING_SECONDS > MIN_TRAINING_SECONDS
            ? "train.upload.footnote"
            : "train.upload.footnoteNoRec",
          {
            min: minutes(t, MIN_TRAINING_SECONDS),
            rec: Math.round(RECOMMENDED_TRAINING_SECONDS / 60),
            max: Math.round(MAX_TRAINING_SECONDS / 60),
          },
        )}
      >
        {passesLabel !== "Unlimited" &&
          !(Number(passesLabel) > 0) &&
          canUpsellToCreator(user) && (
            <div className="tw-rounded-[18px] tw-bg-white/10 tw-px-3.5 tw-py-3 tw-text-[13px] tw-font-bold tw-leading-snug">
              {t("train.upload.creatorNote")}
            </div>
          )}
        <button
          type="button"
          onClick={goToNextStage}
          disabled={files.length === 0}
          className="tw-h-[58px] tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-base tw-font-extrabold tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        >
          {t("train.upload.continue")}
        </button>
      </TrainSummaryCard>
    </div>
  );
};

export default TrainUploadStage;
